// Utility functions for deep linking to mobile app
export const APP_SCHEMES = {
  IOS: 'vault://',
  ANDROID: 'vault://'
};
export const APP_STORE_URLS = {
  IOS: 'https://apps.apple.com/app/vault-roamingiq/id6751550987', // Real App Store URL
  ANDROID: 'https://play.google.com/store/apps/details?id=app.roamingIQvault' // Replace with actual Play Store URL
};
/**
 * Detects if the user is on a mobile device
 */
export const isMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /android|iPad|iPhone|iPod|blackberry|iemobile|opera mini/i.test(userAgent);
};
/**
 * Detects if the user is on iOS
 */
export const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};
/**
 * Detects if the user is on Android
 */
export const isAndroid = () => {
  return /Android/.test(navigator.userAgent);
};
/**
 * Detects if this page load was likely from a failed deep link
 * This happens when a deep link fails to open the app and falls back to web
 */
export const isFailedDeepLink = () => {
  // Check if there's a referrer that suggests this was a deep link fallback
  const referrer = document.referrer;
  const userAgent = navigator.userAgent;
  // On iOS, Universal Links that fail to open app typically have no referrer
  // or come from system contexts
  if (isIOS()) {
    return !referrer || referrer === '' || referrer.includes('apple.com');
  }
  // On Android, failed deep links might have specific referrer patterns
  if (isAndroid()) {
    return !referrer || referrer === '' || 
           referrer.includes('android-app://') || 
           referrer.includes('google.com');
  }
  return false;
};
/**
 * Detects if the iOS app is likely installed by attempting to open it
 * and monitoring if the page becomes hidden (indicating app opened)
 * @param {function} onInstalled - Callback if app appears to be installed
 * @param {function} onNotInstalled - Callback if app appears not to be installed
 * @param {number} timeout - How long to wait before assuming app is not installed (ms)
 */
export const detectIOSAppInstalled = (onInstalled, onNotInstalled, timeout = 2500) => {
  if (!isIOS()) {
    console.log('Not on iOS device - app detection not applicable');
    if (onNotInstalled) onNotInstalled();
    return false;
  }
  let appOpened = false;
  let startTime = Date.now();
  // Listen for page visibility changes
  const handleVisibilityChange = () => {
    if (document.hidden) {
      console.log('📱 Page hidden - iOS app likely opened');
      appOpened = true;
      cleanup();
      if (onInstalled) onInstalled();
    }
  };
  // Listen for page focus events as backup
  const handlePageFocus = () => {
    if (!appOpened && Date.now() - startTime > 1000) {
      console.log('📱 Page refocused - user likely returned from app attempt');
      cleanup();
      if (onNotInstalled) onNotInstalled();
    }
  };
  const cleanup = () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('focus', handlePageFocus);
    window.removeEventListener('pageshow', handlePageFocus);
  };
  // Add event listeners
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('focus', handlePageFocus);
  window.addEventListener('pageshow', handlePageFocus);
  // Try to open the app with custom scheme
  try {
    const scheme = APP_SCHEMES.IOS;
    const testUrl = `${scheme}/test`; // Use a simple test path with proper formatting
    console.log('🧪 Testing iOS app installation with:', testUrl);
    // Create hidden iframe to trigger app opening attempt
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = testUrl;
    document.body.appendChild(iframe);
    // Clean up iframe quickly
    setTimeout(() => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe);
      }
    }, 100);
  } catch (e) {
    console.log('❌ Failed to test app installation:', e);
    cleanup();
    if (onNotInstalled) onNotInstalled();
    return false;
  }
  // Set timeout to assume app is not installed
  setTimeout(() => {
    if (!appOpened) {
      console.log('⏰ Timeout reached - iOS app likely not installed');
      cleanup();
      if (onNotInstalled) onNotInstalled();
    }
  }, timeout);
  return true;
};
/**
 * Shows an alert to inform iOS users that the app is available
 * @param {boolean} isInstalled - Whether the app is detected as installed
 * @param {string} currentPath - Current page path to preserve in deep link
 */
export const showIOSAppAlert = (isInstalled = false, currentPath = null) => {
  if (!isIOS()) {
    console.log('Not on iOS - app alert not applicable');
    return false;
  }
  const path = currentPath || window.location.pathname + window.location.search;
  if (isInstalled) {
    // Show alert for installed app
    const userChoice = window.confirm(
      "📱 Vault App Detected!\n\n" +
      "It looks like you have the Vault app installed. Would you like to open it for a better experience?\n\n" +
      "Tap OK to open the app, or Cancel to stay on the website."
    );
    if (userChoice) {
      console.log('✅ User chose to open installed app');
      handleAppRedirect(path);
      return true;
    } else {
      console.log('📱 User chose to stay on website');
      return false;
    }
  } else {
    // Show alert for non-installed app  
    const userChoice = window.confirm(
      "📱 Get the Vault Mobile App!\n\n" +
      "For the best experience, download our mobile app from the App Store. It's faster and has exclusive features!\n\n" +
      "Tap OK to go to the App Store, or Cancel to continue on the website."
    );
    if (userChoice) {
      console.log('🏪 User chose to go to App Store');
      redirectToAppStore();
      return true;
    } else {
      console.log('📱 User chose to stay on website');
      return false;
    }
  }
};
/**
 * Properly encodes a path with query parameters for custom scheme URLs
 * @param {string} path - The path to encode (e.g., '/terms?param=value+with+plus')
 * @returns {string} - Properly encoded path for custom schemes
 */
export const encodePathForCustomScheme = (path) => {
  if (!path || !path.includes('?')) {
    return path; // No query parameters to encode
  }
  const [pathname, queryString] = path.split('?');
  if (!queryString) {
    return path;
  }
  // Manual parsing to preserve + characters and other special characters
  const params = [];
  const paramPairs = queryString.split('&');
  for (const pair of paramPairs) {
    const [key, value = ''] = pair.split('=');
    // Decode only the key and value, but preserve + characters by encoding them as %2B
    const decodedKey = decodeURIComponent(key);
    const decodedValue = decodeURIComponent(value);
    // Re-encode the key and value, but specifically encode + as %2B to preserve it
    const encodedKey = encodeURIComponent(decodedKey).replace(/\+/g, '%2B');
    const encodedValue = encodeURIComponent(decodedValue).replace(/\+/g, '%2B');
    params.push(`${encodedKey}=${encodedValue}`);
  }
  const encodedPath = `${pathname}?${params.join('&')}`;
  console.log('🔧 Encoded path for custom scheme:', { 
    original: path, 
    encoded: encodedPath,
    preservedPlus: encodedPath.includes('%2B')
  });
  return encodedPath;
};
/**
 * Attempts to open the app with deep linking
 * @param {string} path - The path to navigate to in the app (e.g., 'login', 'terms', '/terms?param=value')
 */
export const tryOpenApp = (path = 'login') => {
  const scheme = isIOS() ? APP_SCHEMES.IOS : APP_SCHEMES.ANDROID;
  // Ensure path starts with / and properly format the deep link URL
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  // Properly encode the path for custom scheme handling
  const encodedPath = encodePathForCustomScheme(cleanPath);
  const deepLinkUrl = `${scheme}${encodedPath}`;
  console.log('🔗 Attempting to open app with URL:', deepLinkUrl);
  if (isIOS()) {
    // For iOS, we can use Universal Links which are already configured
    // The apple-app-site-association file handles this automatically
    return true; // Let Universal Links handle it
  } else if (isAndroid()) {
    // For Android, try to open with custom scheme
    try {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = deepLinkUrl;
      document.body.appendChild(iframe);
      setTimeout(() => {
        document.body.removeChild(iframe);
      }, 100);
      return true;
    } catch (e) {
      console.log('Failed to open app:', e);
      return false;
    }
  }
  return false;
};
/**
 * Redirects to app store if app is not installed
 */
export const redirectToAppStore = () => {
  let storeUrl;
  if (isIOS()) {
    storeUrl = APP_STORE_URLS.IOS;
  } else if (isAndroid()) {
    storeUrl = APP_STORE_URLS.ANDROID;
  }
  if (storeUrl) {
    window.location.href = storeUrl;
  }
};
/**
 * Convenience function to redirect to app using current page path with query parameters
 * @param {number} fallbackDelay - Time to wait before showing fallback (ms)
 * @param {function} onFallback - Optional callback function to execute if app doesn't open
 */
export const handleAppRedirectCurrentPage = (fallbackDelay = 2000, onFallback) => {
  const currentPath = window.location.pathname + window.location.search + window.location.hash;
  return handleAppRedirect(currentPath, fallbackDelay, onFallback);
};
/**
 * Main function to handle app redirect logic
 * @param {string} path - The path to navigate to in the app (e.g., '/terms?param=value')
 * @param {number} fallbackDelay - Time to wait before showing fallback (ms)
 * @param {function} onFallback - Optional callback function to execute if app doesn't open
 */
export const handleAppRedirect = (path = '/login', fallbackDelay = 2000, onFallback) => {
  if (!isMobile()) {
    console.log('Not on mobile device, staying on web');
    return false;
  }
  let appOpened = false;
  const startTime = Date.now();
  // Add visibility change listener to detect if app opened
  const handleVisibilityChange = () => {
    if (document.hidden) {
      console.log('Page hidden - app likely opened');
      appOpened = true;
    }
  };
  document.addEventListener('visibilitychange', handleVisibilityChange);
  // For iOS, try Universal Links first, then custom scheme as fallback
  if (isIOS()) {
    console.log('iOS detected - attempting app redirect');
    const scheme = APP_SCHEMES.IOS;
    // Ensure path starts with / and properly format the deep link URL
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    // Properly encode the path for custom scheme handling
    const encodedPath = encodePathForCustomScheme(cleanPath);
    const deepLinkUrl = `${scheme}${encodedPath}`;
    console.log('🔗 Opening app with URL:', deepLinkUrl);
    // Try to open with custom scheme
    window.location.href = deepLinkUrl;
    // Fallback check
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (!appOpened && (Date.now() - startTime >= fallbackDelay)) {
        console.log('App may not be installed on iOS');
        if (onFallback) {
          onFallback();
        } else {
          console.log('Consider redirecting to App Store');
          // redirectToAppStore();
        }
      }
    }, fallbackDelay);
    return true;
  }
  // For Android, try custom scheme and fallback to Play Store
  if (isAndroid()) {
    console.log('Android detected - attempting app redirect');
    const success = tryOpenApp(path);
    if (!success) {
      console.log('Failed to open app immediately');
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (onFallback) {
        onFallback();
      } else {
        redirectToAppStore();
      }
      return false;
    }
    // Check if app opened after delay
    setTimeout(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (!appOpened) {
        console.log('App may not be installed on Android');
        if (onFallback) {
          onFallback();
        } else {
          redirectToAppStore();
        }
      }
    }, fallbackDelay);
    return true;
  }
  return false;
};
/**
 * Detects iOS app installation and shows appropriate alert
 * This is a convenience function that combines detection and user notification
 * @param {string} currentPath - Current page path to preserve in deep link
 * @param {number} detectionTimeout - How long to wait for app detection (ms)
 */
export const detectAndAlertIOSApp = (currentPath = null, detectionTimeout = 2500) => {
  if (!isIOS()) {
    console.log('Not on iOS device');
    return false;
  }
  console.log('🔍 Detecting iOS app installation...');
  detectIOSAppInstalled(
    // App appears to be installed
    () => {
      console.log('✅ App detected as installed - showing installed app alert');
      showIOSAppAlert(true, currentPath);
    },
    // App appears not to be installed
    () => {
      console.log('❌ App not detected - showing app store alert');
      showIOSAppAlert(false, currentPath);
    },
    detectionTimeout
  );
  return true;
};
/**
 * Silently attempts to redirect to iOS app if installed
 * No user prompts - just tries to open the app automatically
 * @param {string} currentPath - Current page path to preserve in deep link
 * @param {function} onAppOpened - Callback when app successfully opens
 * @param {function} onAppNotInstalled - Callback when app is not installed
 * @param {number} detectionTimeout - How long to wait for app detection (ms)
 */
export const silentlyRedirectToIOSApp = (currentPath = null, onAppOpened = null, onAppNotInstalled = null, detectionTimeout = 2000) => {
  if (!isIOS()) {
    console.log('Not on iOS device - silent redirect not applicable');
    if (onAppNotInstalled) onAppNotInstalled();
    return false;
  }
  const path = currentPath || window.location.pathname + window.location.search;
  console.log('🔄 Attempting silent redirect to iOS app for path:', path);
  detectIOSAppInstalled(
    // App appears to be installed - redirect silently
    () => {
      console.log('✅ App detected - redirecting silently');
      handleAppRedirect(path);
      if (onAppOpened) onAppOpened();
    },
    // App appears not to be installed - continue with web
    () => {
      console.log('📱 App not detected - continuing with web experience');
      if (onAppNotInstalled) onAppNotInstalled();
    },
    detectionTimeout
  );
  return true;
};
/**
 * Checks if we should attempt automatic app redirect for current page
 * Only redirects on key pages and respects user preferences
 * @param {string} pathname - Current page path
 * @returns {boolean} Whether automatic redirect should be attempted
 */
export const shouldAttemptAutoRedirect = (pathname) => {
  // Pages where app experience is significantly better
  const autoRedirectPaths = ['/login', '/terms', '/signup', '/selectplan', '/checkout', '/myaccount'];
  // Check if current path should trigger auto-redirect
  const shouldRedirect = autoRedirectPaths.some(path => pathname.startsWith(path));
  // Check if user has previously declined auto-redirect
  const userDeclinedAutoRedirect = localStorage.getItem('vault_declined_auto_redirect') === 'true';
  // Only redirect if path supports it and user hasn't declined
  return shouldRedirect && !userDeclinedAutoRedirect && isMobile() && isIOS();
};
/**
 * Handles wifikey.link URLs by offering to redirect to the Vault app
 * @param {string} wifiKeyUrl - The full wifikey.link URL to redirect to if user cancels
 * @param {string} appPath - The path to open in the app (default: '/wifikey')
 * @returns {boolean} - Whether the redirect was handled
 */
export const handleWifiKeyLink = (wifiKeyUrl, appPath = '/wifikey') => {
  if (!isMobile()) {
    console.log('Not on mobile device - wifikey.link redirect not applicable');
    return false;
  }

  // Extract any query parameters from the wifikey.link URL to pass to the app
  try {
    const url = new URL(wifiKeyUrl);
    const queryParams = url.search; // Gets ?param=value part
    const fullAppPath = appPath + queryParams;
    
    console.log('🔗 WiFi Key Link detected:', wifiKeyUrl);
    console.log('📱 App path would be:', fullAppPath);

    if (isIOS()) {
      // For iOS, detect if app is installed first
      detectIOSAppInstalled(
        // App appears to be installed
        () => {
          console.log('✅ Vault app detected - showing WiFi Key redirect alert');
          const userChoice = window.confirm(
            "📱 Open in Vault App?\n\n" +
            "You're about to visit a WiFi setup link. Would you like to open it in the Vault app for a better experience?\n\n" +
            "Tap OK to open in the app, or Cancel to continue to the website."
          );
          
          if (userChoice) {
            console.log('✅ User chose to open WiFi Key in app');
            handleAppRedirect(fullAppPath);
          } else {
            console.log('🌐 User chose to continue to wifikey.link website');
            window.location.href = wifiKeyUrl;
          }
        },
        // App appears not to be installed
        () => {
          console.log('❌ Vault app not detected - redirecting directly to wifikey.link');
          window.location.href = wifiKeyUrl;
        }
      );
      return true;
    } else if (isAndroid()) {
      // For Android, show the choice immediately since app detection is less reliable
      const userChoice = window.confirm(
        "📱 Open in Vault App?\n\n" +
        "You're about to visit a WiFi setup link. Would you like to try opening it in the Vault app?\n\n" +
        "Tap OK to try the app, or Cancel to continue to the website."
      );
      
      if (userChoice) {
        console.log('✅ User chose to try opening WiFi Key in app');
        // Try to open app, with fallback to website if it fails
        handleAppRedirect(fullAppPath, 3000, () => {
          console.log('🌐 App failed to open - redirecting to wifikey.link');
          window.location.href = wifiKeyUrl;
        });
      } else {
        console.log('🌐 User chose to continue to wifikey.link website');
        window.location.href = wifiKeyUrl;
      }
      return true;
    }
  } catch (e) {
    console.error('❌ Error parsing wifikey.link URL:', e);
    // If there's an error parsing, just redirect to the original URL
    window.location.href = wifiKeyUrl;
    return true;
  }

  return false;
};

/**
 * Checks if the current URL or a given URL is a wifikey.link domain
 * @param {string} url - Optional URL to check, defaults to current location
 * @returns {boolean} - Whether the URL is a wifikey.link domain
 */
export const isWifiKeyLink = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.endsWith('.wifikey.link') || urlObj.hostname === 'wifikey.link';
  } catch (e) {
    return false;
  }
};

/**
 * Auto-detects wifikey.link URLs and handles app redirect
 * Call this on page load to automatically handle wifikey.link redirects
 * @param {string} customAppPath - Custom app path to use instead of '/wifikey'
 * @returns {boolean} - Whether a wifikey.link redirect was handled
 */
export const autoHandleWifiKeyLinks = (customAppPath = '/wifikey') => {
  const currentUrl = window.location.href;
  
  if (isWifiKeyLink(currentUrl)) {
    console.log('🔍 WiFi Key Link detected on page load:', currentUrl);
    return handleWifiKeyLink(currentUrl, customAppPath);
  }
  
  return false;
};

/**
 * Manual test function for iOS app detection (for debugging)
 * Call this from browser console: window.testIOSApp()
 */
export const testIOSAppDetection = () => {
  console.group('🧪 Manual iOS App Detection Test');
  console.log('Device:', isIOS() ? 'iOS' : 'Not iOS');
  console.log('Mobile:', isMobile() ? 'Yes' : 'No');
  if (!isIOS()) {
    console.log('❌ Test only works on iOS devices');
    console.groupEnd();
    return false;
  }
  console.log('🔍 Starting detection test...');
  detectAndAlertIOSApp();
  console.groupEnd();
  return true;
};
// Make test functions available globally for debugging
if (typeof window !== 'undefined') {
  window.testIOSApp = testIOSAppDetection;
  window.testWifiKeyLink = (url) => {
    console.log('🧪 Testing WiFi Key Link redirect with URL:', url || 'current page');
    return handleWifiKeyLink(url || window.location.href);
  };
}
